import * as styles from "./ClientList.css";

import { Provider, createStore, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@frontend/components/atoms/AlertDialog";
import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Heading } from "@frontend/components/atoms/Heading";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Panel } from "@frontend/components/atoms/Panel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { Stack } from "@frontend/components/atoms/Stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { Pagination } from "@frontend/components/molecules/Pagination";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import {
  DataTable,
  createDataTableColumnHelper,
} from "@frontend/components/organisms/DataTable";
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderHeading,
} from "@frontend/components/organisms/PageHeader";
import { useDebouncedCallback } from "@frontend/hooks/useDebouncedCallback";
import { useRowNavigation } from "@frontend/hooks/useRowNavigation";
import { url } from "@frontend/lib/http";
import { Trans, useLingui } from "@lingui/react/macro";
import type { SortingState } from "@tanstack/react-table";

import { ClientEditorDialog } from "./ClientEditorDialog";
import { ClientQuickview } from "./components/ClientQuickview";
import {
  archiveClientAtom,
  clientListAtom,
  deleteClientAtom,
  loadClientListOptionsAtom,
  prefetchClientListAtom,
  reloadClientListAtom,
  setClientListQueryAtom,
} from "./stores/list";
import type { ClientListState } from "./stores/list";
import {
  closeClientQuickviewAtom,
  openClientQuickviewAtom,
  refreshClientQuickviewAtom,
  shownClientQuickviewIdAtom,
} from "./stores/quickview";
import type { ClientListColumn, ClientListFilter, ClientRow } from "./types";

const helper = createDataTableColumnHelper<ClientRow>();

const PAGE_SIZES = [10, 25, 50, 100] as const;

const SEARCH_DEBOUNCE = 300;

function toSortQuery(
  sorting: SortingState,
): Pick<ClientListState, "sort" | "direction"> {
  const [first] = sorting;

  return {
    sort: (first?.id as ClientListColumn | undefined) ?? null,
    direction: first?.desc ? "desc" : "asc",
  };
}

export type ClientListProps = {
  filter?: ClientListFilter;
  onOpen?: (clientId: number | null) => void;
};

export function ClientList({
  filter: initialFilter = "active",
  onOpen,
}: ClientListProps): JSX.Element {
  const { t } = useLingui();

  const {
    rows,
    total,
    page,
    perPage,
    search,
    filter,
    sort,
    direction,
    criteria,
    filterOptions,
    loading,
    error,
  } = useAtomValue(clientListAtom);

  const reload = useSetAtom(reloadClientListAtom);
  const loadOptions = useSetAtom(loadClientListOptionsAtom);
  const setQuery = useSetAtom(setClientListQueryAtom);
  const prefetch = useSetAtom(prefetchClientListAtom);
  const archive = useSetAtom(archiveClientAtom);
  const remove = useSetAtom(deleteClientAtom);
  const openQuickview = useSetAtom(openClientQuickviewAtom);
  const closeQuickview = useSetAtom(closeClientQuickviewAtom);
  const refreshQuickview = useSetAtom(refreshClientQuickviewAtom);
  const shownClientId = useAtomValue(shownClientQuickviewIdAtom);

  const pageCount = Math.max(1, Math.ceil(total / perPage));

  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const [opened, setOpened] = useState<number | null | undefined>(undefined);

  const opener = useRef(onOpen);

  useEffect(() => {
    opener.current = onOpen;
  });

  const open = useCallback((clientId: number | null) => {
    (opener.current ?? setOpened)(clientId);
  }, []);
  const [pendingArchive, setPendingArchive] = useState<ClientRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ClientRow | null>(null);
  const [quickviewOpen, setQuickviewOpen] = useState(false);

  const showQuickview = (client: ClientRow) => {
    setQuickviewOpen(true);
    void openQuickview(client.id);
  };

  const navigation = useRowNavigation({
    rowCount: rows.length,
    page,
    pageCount,
    onPageChange: (next) => setQuery({ page: next }),
    onOpen: (index) => {
      const client = rowsRef.current[index];

      if (client) {
        showQuickview(client);
      }
    },
  });

  const hideQuickview = () => {
    setQuickviewOpen(false);
    closeQuickview();
    navigation.setActiveIndex(-1);
  };

  const [typed, setTyped] = useState(search);

  const searchLater = useDebouncedCallback(
    (next: string) => void setQuery({ search: next }),
    SEARCH_DEBOUNCE,
  );

  useEffect(() => {
    void setQuery({ filter: initialFilter });
    void loadOptions();
  }, [setQuery, loadOptions, initialFilter]);

  const sorting = useMemo(
    () => (null === sort ? [] : [{ id: sort, desc: "desc" === direction }]),
    [sort, direction],
  );

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("id", {
          header: t`Id`,
          meta: { width: "90px", filter: "number" },
        }),
        helper.accessor("displayName", {
          header: t`Name`,
          meta: { minWidth: "150px", truncate: true, filter: "text" },
          cell: ({ getValue, row }) => (
            <span>
              {getValue() ?? "-"}{" "}
              {row.original.isOrganization ? null : (
                <Icon name="User" size="sm" />
              )}
            </span>
          ),
        }),
        helper.accessor("typeLabel", {
          header: t`Kind`,
          meta: {
            minWidth: "120px",
            maxWidth: "180px",
            truncate: true,
            filter: { type: "select", options: filterOptions.types },
          },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor(
          (client) =>
            client.accountants
              .map((accountant) => accountant.employeeLabel)
              .filter(Boolean)
              .join(", "),
          {
            id: "accountantLabel",
            header: t`In charge`,
            meta: { minWidth: "150px", truncate: true, filter: "text" },
            cell: ({ getValue }) => (
              <span className={styles.accountants}>
                {getValue<string>() || "-"}
              </span>
            ),
          },
        ),
        helper.accessor("statusLabel", {
          header: t`Status`,
          meta: {
            minWidth: "80px",
            maxWidth: "120px",
            truncate: true,
            filter: { type: "select", options: filterOptions.statuses },
          },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("regionLabel", {
          header: t`Region`,
          meta: {
            minWidth: "120px",
            truncate: true,
            filter: { type: "select", options: filterOptions.regions },
          },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.display({
          id: "projects",
          header: t`Projects`,
          enableSorting: false,
          meta: { width: "80px" },
          cell: ({ row }) => (
            <Badge
              variant={0 < row.original.activeProjectCount ? "info" : "neutral"}
              size="sm"
            >
              {row.original.activeProjectCount} / {row.original.projectCount}
            </Badge>
          ),
        }),
        helper.display({
          id: "actions",
          header: t`Action`,
          enableSorting: false,
          meta: { width: "130px" },
          cell: ({ row }) => (
            <span
              className={styles.rowActions}
              // The row itself opens the drawer, and these do something else.
              onClick={(event) => event.stopPropagation()}
            >
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Edit`}
                      onClick={() => open(row.original.id)}
                    >
                      <Icon name="Pencil" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="top">{t`Edit`}</TooltipContent>
              </Tooltip>

              {/* Archiving is a status, so a client already archived is not
                  archived again, and one carrying a project is not deleted:
                  the two rules the legacy list applies to its own row. */}
              {row.original.archived ? null : (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t`Archive`}
                        onClick={() => setPendingArchive(row.original)}
                      >
                        <Icon name="Archive" size="sm" />
                      </Button>
                    }
                  />

                  <TooltipContent side="top">{t`Archive`}</TooltipContent>
                </Tooltip>
              )}

              {0 === row.original.projectCount ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t`Delete`}
                        onClick={() => setPendingDelete(row.original)}
                      >
                        <Icon name="Trash2" size="sm" />
                      </Button>
                    }
                  />

                  <TooltipContent side="top">{t`Delete`}</TooltipContent>
                </Tooltip>
              ) : null}
            </span>
          ),
        }),
      ]),
    [t, filterOptions, open],
  );

  const title = "archived" === filter ? t`Archived clients` : t`Clients`;

  // Read into a name of their own: an expression inside <Trans> is renamed by
  // the React compiler, and the message id then misses the catalogue. A client
  // with no name is called by its identifier, which is what the row shows.
  const archivedName = named(pendingArchive);
  const deletedName = named(pendingDelete);

  return (
    <Stack gap="md">
      <PageHeader variant="plain" icon="Briefcase">
        <PageHeaderContent>
          <PageHeaderHeading>
            <Heading name={title} />
          </PageHeaderHeading>
        </PageHeaderContent>

        <PageHeaderActions>
          <Button
            variant="link"
            leftSection={<Icon name="Wand2" size="sm" />}
            onClick={() => open(null)}
          >
            {t`New client`}
          </Button>

          <Button
            variant="link"
            leftSection={<Icon name="Download" size="sm" />}
            onClick={() => {
              window.location.href = url("api_clients_export", {}, { filter });
            }}
          >
            {t`Export`}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      {error ? (
        <p className={styles.alert} role="alert">
          {error}
        </p>
      ) : null}

      <Panel overflow="visible">
        <Toolbar wrap={false}>
          <ToolbarSection className={styles.filters}>
            <Input
              className={styles.search}
              value={typed}
              placeholder={t`Search a name or an identifier`}
              leftSection={<Icon name="Search" size="sm" />}
              clearable
              onValueChange={(next) => {
                setTyped(next);
                searchLater(next);
              }}
            />

            <Select
              items={[
                { label: t`Active`, value: "active" },
                { label: t`Archived`, value: "archived" },
                { label: t`All`, value: "all" },
              ]}
              value={filter}
              onValueChange={(next) =>
                void setQuery({ filter: next as ClientListFilter })
              }
            >
              <SelectTrigger className={styles.filter}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="active">{t`Active`}</SelectItem>
                <SelectItem value="archived">{t`Archived`}</SelectItem>
                <SelectItem value="all">{t`All`}</SelectItem>
              </SelectContent>
            </Select>
          </ToolbarSection>

          <ToolbarSection>
            <Select
              items={PAGE_SIZES.map((size) => ({
                label: String(size),
                value: String(size),
              }))}
              value={String(perPage)}
              onValueChange={(next) => void setQuery({ perPage: Number(next) })}
            >
              <SelectTrigger className={styles.pageSize}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {PAGE_SIZES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ToolbarSection>
        </Toolbar>

        <DataTable
          columns={columns}
          data={rows}
          getRowId={(client) => String(client.id)}
          loading={loading}
          loadingRowCount={perPage}
          emptyMessage={<Trans>No client matches this search.</Trans>}
          columnBorders
          bordered={false}
          // The table opens no scroll port of its own, so the page scrolls and
          // the pagination under it stays reachable.
          scroll="page"
          stickyHeader
          stickyOffset="var(--layout-header-height, 0px)"
          sorting={sorting}
          onSortingChange={(next) => void setQuery(toSortQuery(next))}
          // A pointer resting on a header reads its ordering before the click.
          onSortPrefetch={(next) => prefetch({ page: 1, ...toSortQuery(next) })}
          columnFilters={criteria}
          onColumnFiltersChange={(next) => void setQuery({ criteria: next })}
          activeRowIndex={navigation.activeIndex}
          onRowClick={(client) => {
            navigation.setActiveIndex(rows.indexOf(client));
            showQuickview(client);
          }}
          rowMenu={(client) => [
            { id: "edit", label: t`Edit`, icon: "Pencil" },
            {
              id: "archive",
              label: t`Archive`,
              icon: "Archive",
              disabled: client.archived,
            },
            {
              id: "delete",
              label: t`Delete`,
              icon: "Trash2",
              disabled: 0 < client.projectCount,
              separatorBefore: true,
            },
          ]}
          onRowMenuSelect={(id, client) => {
            if ("edit" === id) {
              open(client.id);
            }

            if ("archive" === id) {
              setPendingArchive(client);
            }

            if ("delete" === id) {
              setPendingDelete(client);
            }
          }}
        />
      </Panel>

      <Pagination
        page={page}
        pageCount={pageCount}
        totalItems={total}
        pageSize={perPage}
        align="end"
        onPageChange={(next) => void setQuery({ page: next })}
        onPagePrefetch={(next) => prefetch({ page: next })}
      />

      <ClientQuickview
        open={quickviewOpen}
        onOpenChange={(next) => {
          if (!next) {
            hideQuickview();
          }
        }}
        onEdit={(clientId) => open(clientId)}
        onArchive={(clientId) =>
          setPendingArchive(
            rows.find((client) => clientId === client.id) ?? null,
          )
        }
      />

      {/* Keyed on the record: the editor loads on mount, and two rows are two
          different clients. Nothing opens here when a caller took that over. */}
      {undefined !== onOpen || undefined === opened ? null : (
        <ClientWindow
          key={opened ?? "new"}
          clientId={opened}
          onClose={() => setOpened(undefined)}
          onSaved={(savedId) => {
            setOpened(undefined);
            void refreshQuickview(savedId);
            void reload();
          }}
        />
      )}

      <AlertDialog
        open={null !== pendingArchive}
        onOpenChange={(next) => {
          if (!next) {
            setPendingArchive(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Archive this client?</Trans>
            </AlertDialogTitle>

            <AlertDialogDescription>
              <Trans>
                {archivedName} leaves the list of current clients. Its projects
                and its history stay, and giving it another status brings it
                back.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>

            <AlertDialogAction
              onClick={() => {
                if (pendingArchive) {
                  const archived = pendingArchive.id;

                  // Read once the status has actually moved, otherwise the
                  // drawer fills again with the client as it was.
                  void archive(archived).then(() => refreshQuickview(archived));
                }

                setPendingArchive(null);
              }}
            >
              <Trans>Archive</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={null !== pendingDelete}
        onOpenChange={(next) => {
          if (!next) {
            setPendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this client?</Trans>
            </AlertDialogTitle>

            <AlertDialogDescription>
              <Trans>
                {deletedName} goes with its contact persons, the people
                following it and its commercial conditions. This cannot be
                undone.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>

            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) {
                  // The panel would otherwise stand on a record that is gone.
                  if (pendingDelete.id === shownClientId) {
                    hideQuickview();
                  }

                  void remove(pendingDelete.id);
                }

                setPendingDelete(null);
              }}
            >
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Stack>
  );
}

function named(client: ClientRow | null): string {
  if (null === client) {
    return "";
  }

  return client.displayName ?? `#${client.id}`;
}

function ClientWindow({
  clientId,
  onClose,
  onSaved,
}: {
  clientId: number | null;
  onClose: () => void;
  onSaved: (clientId: number) => void;
}): JSX.Element {
  const [store] = useState(() => createStore());

  return (
    <Provider store={store}>
      <ClientEditorDialog
        open
        onOpenChange={(next) => {
          if (!next) {
            onClose();
          }
        }}
        clientId={clientId}
        onSaved={onSaved}
        onCancel={onClose}
      />
    </Provider>
  );
}
